import MainLayout from '@components/Shared/Layout';
import { ROUTE_PATHS } from '@constants/routes';
import { FOLDER_BASE, VIEWER_BASE } from '@lib/routing/projectPath';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import AppSettingsPage from '@pages/AppSettings';
import CustomPoiTypesPage from '@pages/CustomPoiTypes';
import PoiTypeDetailPage from '@pages/PoiTypeDetail';
import HomePage from '@pages/Home';
import LoginPage from '@pages/Login';
import ProjectOverviewPage from '@pages/ProjectOverview';
import ProjectsPage from '@pages/Projects';
import InspectionsPage from '@pages/Inspections';
import ReportBrandingPage from '@pages/ReportBranding';
import ReportDesignDetailPage from '@pages/ReportDesignDetail';
import ReportDesignsPage from '@pages/ReportDesigns';
import ServerConfigPage from '@pages/ServerConfig';
import SettingsPage from '@pages/Settings';
import SsoCallbackPage from '@pages/SsoCallback';
import TeamDetailPage from '@pages/TeamDetail';
import TeamsPage from '@pages/Teams';
import UsersPage from '@pages/Users';
import ViewerPage from '@pages/Viewer';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import InspectionDetailPage from '@pages/InspectionDetail';
import LegacyProjectRedirect from './LegacyProjectRedirect';

/**
 * `/folders/*` addresses a folder and a project alike — the trailing uuid is what tells
 * them apart, and a splat cannot express that as two route patterns.
 */
const FolderRoute = () => {
  const { projectId, inspectionId } = useProjectRoute();

  if (!projectId) return <ProjectsPage />;

  // An inspection in the URL is a page of its own, POI tail included — the project page's
  // tabs never render one.
  return inspectionId ? <InspectionDetailPage /> : <ProjectOverviewPage />;
};

const AppRouter = () => {
  return (
    <Router>
      <Routes>
        <Route path={ROUTE_PATHS.LOGIN} element={<LoginPage />} />
        {/* Outside the layout, whose guard sends anybody not signed in to the sign-in page and
            would drop the answer this page is opened with. */}
        <Route path={ROUTE_PATHS.SSO_CALLBACK} element={<SsoCallbackPage />} />

        <Route element={<MainLayout />}>
          <Route path={ROUTE_PATHS.HOME} element={<HomePage />} />

          <Route path={ROUTE_PATHS.FOLDERS} element={<FolderRoute />} />
          <Route path={ROUTE_PATHS.FOLDER} element={<FolderRoute />} />

          <Route path={ROUTE_PATHS.PROJECTS} element={<ProjectsPage />} />
          <Route path={ROUTE_PATHS.LEGACY_PROJECT} element={<LegacyProjectRedirect base={FOLDER_BASE} />} />

          <Route path={ROUTE_PATHS.INSPECTIONS} element={<InspectionsPage />} />
          <Route path={ROUTE_PATHS.LEGACY_RECENT_INSPECTIONS} element={<Navigate to={ROUTE_PATHS.INSPECTIONS} replace />} />

          <Route path={ROUTE_PATHS.USERS} element={<UsersPage />} />
          <Route path={ROUTE_PATHS.TEAMS} element={<TeamsPage />} />
          <Route path={ROUTE_PATHS.TEAM_DETAIL} element={<TeamDetailPage />} />
          <Route path={ROUTE_PATHS.SETTINGS} element={<SettingsPage />} />
          <Route path={ROUTE_PATHS.SERVER_CONFIG} element={<ServerConfigPage />} />
          <Route path={ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPES} element={<CustomPoiTypesPage />} />
          <Route path={ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE} element={<PoiTypeDetailPage />} />
          <Route path={ROUTE_PATHS.CUSTOMIZATIONS_APP_SETTINGS} element={<AppSettingsPage />} />
          <Route path={ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGNS} element={<ReportDesignsPage />} />
          <Route path={ROUTE_PATHS.CUSTOMIZATIONS_REPORT_DESIGN} element={<ReportDesignDetailPage />} />
          <Route path={ROUTE_PATHS.CUSTOMIZATIONS_REPORT_BRANDING} element={<ReportBrandingPage />} />
        </Route>

        <Route path={ROUTE_PATHS.VIEWER_FOLDER} element={<ViewerPage />} />
        <Route path={ROUTE_PATHS.LEGACY_VIEWER_PROJECT} element={<LegacyProjectRedirect base={VIEWER_BASE} />} />

        <Route path={ROUTE_PATHS.NOT_FOUND} element={<Navigate to={ROUTE_PATHS.HOME} />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
